import fetchClient from './fetchClient';
import { errorHandler } from './utils';


const config = (isExternalPage: boolean = false) => {
  return {
    headers: {
      Authorization:
        'Bearer ' +
        localStorage.getItem(isExternalPage ? 'delivery-token' : 'token'),
    },
  };
};

//  Dashboard (if implemented on the backend)
export const getPurchaseDashboard = async () => {
  const res: any = await fetchClient
    .get('/purchases/dashboard', config())
    .catch(() => null);
  if (!res) return null;
  return res.data;
};

// 📥 Export CSV
export const exportPurchases = async (status: string = '', search: string = '', range: any = null) => {
  const fileConfig: any = {
    headers: {
      Authorization: config(false).headers.Authorization,
    },
    responseType: 'arraybuffer',
  };
  let url = `/purchases/export?status=${encodeURIComponent(status)}&search=${encodeURIComponent(search)}`;
  if (range) {
    url += `&start=${range.startDate}&end=${range.endDate}`;
  }
  const res = await fetchClient.get(url, fileConfig).catch((err) => {
    errorHandler(err);
  });
  if (!res || !res.data) return null;
  return new Blob([res.data], { type: 'text/csv' });
};

//  Paginated list
export const getPaginatedPurchases = async (
  page: number,
  limit: number,
  status: string,
  search: string,
  sortBy: string = 'createdAt',
  range: any = null,
) => {
  let url = `/purchases?limit=${limit}&page=${page}&status=${status}&search=${search}&sortBy=${sortBy}`;
  if (range) {
    url += `&start=${range.startDate}&end=${range.endDate}`;
  }
  const res: any = await fetchClient.get(url, config()).catch(() => null);
  if (!res) return null;
  return res.data;
};

//  All purchases (non-paginated, e.g. for dropdowns)
export const getPurchases = async () => {
  const res: any = await fetchClient
    .get(`/purchases?limit=5000`, config())
    .catch(() => null);
  if (!res) return null;
  return res.data;
};

//  Purchase detail
export const getPurchase = async (id: string) => {
  const res = await fetchClient.get(`/purchases/${id}`, config()).catch(() => null);
  if (!res) return null;
  return res.data;
};

//  Update
export const updatePurchase = async (purchase: any) => {
  const id = purchase._id;
  delete purchase._id;
  const res: any = await fetchClient
    .put(`/purchases/${id}`, purchase, config())
    .catch(errorHandler);
  if (res.error) return res;
  return { data: res ? res.data : null, error: null };
};

//  Creation
export const createPurchase = async (purchase: any) => {
  const res = await fetchClient
    .post('/purchases', purchase, config())
    .catch(errorHandler);
  return res.data;
};

//  Deletion
export const deletePurchase = async (id: string) => {
  const res: any = await fetchClient
    .delete(`/purchases/${id}`, config())
    .catch(errorHandler);
  if (res.error) return res;
  return { data: res ? res.data : null, error: null };
};

export const acceptPurchaseBySupplier = async (token: string) => {
  const res: any = await fetchClient
    .put(`/purchases/accept-by-supplier/${token}`)
    .catch(errorHandler);
  if (!res) return null;
  return res.data;
};

export const markReceived = async (id: string) => {
    const res: any = await fetchClient
        .put(`/purchases/${id}/mark-received`, {}, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const markPartialReceived = async (id: string, receivedItems: { receivedQty: number; itemId?: string }[], receptionDate?: string) => {
    const res: any = await fetchClient
        .put(`/purchases/${id}/mark-partial-received`, { receivedItems, receptionDate }, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

// Cancel a received / partially received purchase: reverses every received
// quantity back out of stock (on its original lot+area) and returns the purchase
// to 'pending'. The backend surfaces a precise error (e.g. missing override
// permission) which errorHandler already toasts.
export const cancelPurchaseReception = async (id: string) => {
    const res: any = await fetchClient
        .put(`/purchases/${id}/cancel-reception`, {}, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const uploadPurchaseInvoice = async (id: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('token');
  const response = await fetch(`${(await import('variables/config')).default.apiUrl}/purchases/${id}/upload-invoice`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Upload failed' }));
    return { error: err.error || 'Upload failed' };
  }
  return response.json();
};

export const getPurchaseInvoiceUrl = async (id: string) => {
  const res: any = await fetchClient
    .get(`/purchases/${id}/invoice-url`, config())
    .catch(() => null);
  if (!res) return null;
  return res.data?.url || null;
};

export const markPaid = async (purchaseId: string, isPaid: boolean) => {
  const res: any = await updatePurchase({
    _id: purchaseId,
    pay: isPaid
  });

  return res;
};



export const getPurchaseBySupplierToken = async (token: string) => {
  const res: any = await fetchClient
    .get(`/purchases/view-purchase/${token}`)
    .catch(errorHandler);
  if (res.error) return null;
  return res ? res.data : null;
}

export const sendPurchaseToSupplier = async (token: string) => {
  const res: any = await fetchClient
    .get(`/purchases/view-purchase/${token}/send-email`)
    .catch(errorHandler);
  if (res.error) return res;
  return { data: res ? res.data : null, error: null };
}
